// Pix: um "canvas" de caracteres para desenhar sprites com código em vez de digitar
// grade na mão. Puro (sem DOM): roda no módulo, na hora do import.
//
//   const s = new Pix(32, 20)
//     .box(0, 6, 32, 14, { fill: 'W', light: 'L', dark: 'D' })
//     .outline('o')
//     .sprite({ o: 'wood0', D: 'wood1', W: 'wood2', L: 'wood3' });
//
// Os dois jeitos (grade digitada e Pix) dão o mesmo PixelSprite. Use o que ficar mais
// legível; os gabaritos usam os dois.

import type { PixelSprite } from './types.js';

export interface BoxStyle {
  fill: string;
  /** Topo e lado esquerdo (luz vem do alto-esquerda). */
  light?: string;
  /** Base e lado direito. */
  dark?: string;
}

export class Pix {
  private readonly grid: string[][];

  constructor(
    readonly w: number,
    readonly h: number,
  ) {
    this.grid = Array.from({ length: h }, () => Array.from({ length: w }, () => '.'));
  }

  /** Começa de uma grade digitada (para retocar com código). */
  static from(rows: readonly string[]): Pix {
    const w = Math.max(0, ...rows.map((r) => r.length));
    const pix = new Pix(w, rows.length);
    rows.forEach((row, y) => [...row].forEach((c, x) => pix.px(x, y, c)));
    return pix;
  }

  get(x: number, y: number): string {
    return this.grid[y]?.[x] ?? '.';
  }

  px(x: number, y: number, c: string): this {
    const row = this.grid[y];
    if (row && x >= 0 && x < this.w) row[x] = c;
    return this;
  }

  rect(x: number, y: number, w: number, h: number, c: string): this {
    for (let j = y; j < y + h; j++) for (let i = x; i < x + w; i++) this.px(i, j, c);
    return this;
  }

  hline(x: number, y: number, w: number, c: string): this {
    return this.rect(x, y, w, 1, c);
  }

  vline(x: number, y: number, h: number, c: string): this {
    return this.rect(x, y, 1, h, c);
  }

  /** Retângulo sombreado: preenche, luz no topo/esquerda, sombra na base/direita. */
  box(x: number, y: number, w: number, h: number, style: BoxStyle): this {
    this.rect(x, y, w, h, style.fill);
    if (style.light) {
      this.hline(x, y, w, style.light);
      this.vline(x, y, h, style.light);
    }
    if (style.dark) {
      this.hline(x, y + h - 1, w, style.dark);
      this.vline(x + w - 1, y, h, style.dark);
    }
    return this;
  }

  /** Disco cheio (copas de árvore, vasos, pratos). */
  disc(cx: number, cy: number, r: number, c: string): this {
    for (let y = Math.floor(cy - r); y <= Math.ceil(cy + r); y++) {
      for (let x = Math.floor(cx - r); x <= Math.ceil(cx + r); x++) {
        if ((x - cx) ** 2 + (y - cy) ** 2 <= r * r + 0.25) this.px(x, y, c);
      }
    }
    return this;
  }

  /** Repete um padrão de caracteres numa linha (ex.: '.k' faz pontilhado). */
  pattern(x: number, y: number, w: number, pattern: string): this {
    for (let i = 0; i < w; i++) {
      const c = pattern[i % pattern.length] ?? '.';
      if (c !== '.') this.px(x + i, y, c);
    }
    return this;
  }

  /** Cola uma grade (ou outro Pix) em (x, y). '.' não sobrescreve. */
  paste(src: readonly string[] | Pix, x: number, y: number): this {
    const rows = src instanceof Pix ? src.rows() : src;
    rows.forEach((row, j) =>
      [...row].forEach((c, i) => {
        if (c !== '.') this.px(x + i, y + j, c);
      }),
    );
    return this;
  }

  /** Contorno automático: todo pixel transparente vizinho (4 lados) de um pintado vira `c`. */
  outline(c: string): this {
    const marks: Array<[number, number]> = [];
    for (let y = 0; y < this.h; y++) {
      for (let x = 0; x < this.w; x++) {
        if (this.get(x, y) !== '.') continue;
        const near = [this.get(x - 1, y), this.get(x + 1, y), this.get(x, y - 1), this.get(x, y + 1)];
        if (near.some((n) => n !== '.' && n !== c)) marks.push([x, y]);
      }
    }
    for (const [x, y] of marks) this.px(x, y, c);
    return this;
  }

  /** Troca um caractere por outro. */
  replace(from: string, to: string): this {
    for (const row of this.grid) for (let i = 0; i < row.length; i++) if (row[i] === from) row[i] = to;
    return this;
  }

  /** Espelha na horizontal (devolve um Pix novo). */
  mirrored(): Pix {
    return Pix.from(this.rows().map((r) => [...r].reverse().join('')));
  }

  rows(): string[] {
    return this.grid.map((r) => r.join(''));
  }

  sprite(palette: Record<string, string>): PixelSprite {
    return { w: this.w, h: this.h, rows: this.rows(), palette };
  }
}

/** Monta um PixelSprite de uma grade digitada (largura = maior linha). */
export function sprite(rows: readonly string[], palette: Record<string, string>): PixelSprite {
  const w = Math.max(0, ...rows.map((r) => r.length));
  return { w, h: rows.length, rows, palette };
}

/** Espelha um sprite na horizontal. */
export function mirror(s: PixelSprite): PixelSprite {
  return { ...s, rows: s.rows.map((r) => [...r].reverse().join('')) };
}
